<template>
  <div class="app-links-page">
    <!-- App Logo Container with background color #f6f6f6 and full width -->
    <div class="app-logo-container">
      <!-- App Logo -->
      <img :src="appLogoURL" alt="App Logo" class="app-logo" />
    </div>

    <!-- Content Left: App Name, Instruction Text, Checkbox -->
    <div class="content-left">
      <!-- App Name -->
      <h1 class="app-name">{{ appName }}</h1>

      <!-- Instruction Text -->
      <p class="instruction-text">Follow these steps to continue</p>

      <!-- Checkbox -->
      <div class="checkbox-container">
        <input type="checkbox" id="savePlace" checked disabled />
        <label for="savePlace">Save my place in the app. A link will be copied to continue to this page.</label>
      </div>
    </div>

    <!-- Single COPY AND OPEN Button for iOS -->
    <a v-if="isIOS" :href="iosAppURL" class="button primary-button" @click="handleCopyAndRedirect(iosStoreURL)"> OPEN APP </a>

    <!-- Single COPY AND OPEN Button for Android -->
    <a v-else-if="isAndroid" :href="androidAppURL" class="button primary-button" @click="handleCopyAndRedirect(androidStoreURL)"> OPEN APP </a>

    <!-- COPY TO CLIPBOARD Button (for desktop) -->
    <button v-else id="copyButton" class="button" @click="handleCopyAndRedirect(iosStoreURL)">COPY TO CLIPBOARD</button>

    <!-- Toast Notification -->
    <div id="toast" role="alert" aria-live="assertive" aria-atomic="true" :class="{ show: toastVisible }">{{ toastMessage }}</div>
  </div>
</template>

<script>
import { getAppData } from '@/api/request/appLinks';

export default {
  name: 'AppLinks',
  data() {
    return {
      appName: '',
      appLogoURL: '',
      isIOS: false,
      isAndroid: false,
      iosIAS: '', // iOS app store ID
      iosAPN: '', // iOS app name
      androidAPN: '', // Android package name
      encodedUserID: '',
      iosAppURL: '',
      iosStoreURL: '',
      androidAppURL: '',
      androidStoreURL: '',
      toastVisible: false,
      toastMessage: '',
      urlToCopy: '',
      deepLink: '',
    };
  },
  created() {
    // Detect device type
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    this.isAndroid = /android/i.test(userAgent);
    this.isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    // Check if deeplink parameter exists
    const deeplink = this.$route.query.deeplink;
    if (deeplink) {
      try {
        // Decode the URI encoded deeplink
        this.deepLink = deeplink; // this.$route.query values are already uri decoded by the vue router
        console.log('decodedDeeplink', this.deepLink);

        // Parse the URL to extract query parameters
        const url = new URL(this.deepLink);
        const params = new URLSearchParams(url.search);

        // Extract required parameters
        this.encodedUserID = params.get('uid') || '';
        this.iosIAS = params.get('iosias') || '';
        this.iosAPN = params.get('iosapn') || '';
        this.androidAPN = params.get('androidapn') || '';
        console.log('this.encodedUserID', this.encodedUserID);
        console.log('this.iosIAS', this.iosIAS);
        console.log('this.iosAPN', this.iosAPN);
        console.log('this.androidAPN', this.androidAPN);
        console.log('this.deeplink', this.deeplink);

        // Validate that we have the required parameters
        if (this.encodedUserID && (this.iosIAS || this.iosAPN || this.androidAPN)) {
          // Fetch app data
          this.fetchAppData(deeplink); // For some reason, fetchAppData does not have access to the set value of this.deeplink. So have to pass the local value in
        } else {
          console.error('Missing required parameters in deeplink');
          this.showToast('Invalid deeplink: missing required parameters');
        }
      } catch (error) {
        console.error('Error parsing deeplink:', error);
        this.showToast('Invalid deeplink format');
      }
    } else {
      console.error('No deeplink or direct parameters provided');
      this.showToast('Missing required parameters');
    }
  },
  methods: {
    async fetchAppData(deeplink) {
      try {
        // Call the API using the getAppData function
        console.log(`[AppLinks] fetchAppData`);
        console.log(`[AppLinks] this.encodedUserID`, this.encodedUserID);
        console.log(`[AppLinks] this.iosIAS`, this.iosIAS);
        console.log(`[AppLinks] this.iosAPN`, this.iosAPN);
        console.log(`[AppLinks] this.androidAPN`, this.androidAPN);
        const response = await getAppData(this.encodedUserID, this.iosIAS, this.iosAPN, this.androidAPN, encodeURIComponent(deeplink));
        console.log('[AppLinks] API response:', response);

        if (response?.data?.data) {
          const data = response.data.data;

          // Update app information from response
          this.appName = data.name || 'App Preview';
          this.appLogoURL = data.logoURL || '/img/brand/logo.png';

          this.androidStoreURL = data.androidStoreURL || '';
          this.androidAppURL = data.androidAppURL || ''; // This is an Android Intent URL. So don't append anything to it.
          this.iosStoreURL = data.iosStoreURL || '';
          // iosAppURL is a Dynamic Link URL. This includes a custom fallback URL in the query parameters in the case the app is not installed
          // the actual URL will open the web browser in which the vobo-api has a route handler that uses the fallback in the URL to
          // redirect this to the app store
          this.iosAppURL = data.iosAppURL;
        } else {
          console.error('Invalid API response format:', response);
          this.appName = 'App Preview';
          this.appLogoURL = '/img/brand/favicon.png'; // Default logo
          this.showToast('Failed to load app data: Invalid response format');
        }
      } catch (error) {
        console.error('Error fetching app data:', error);
        this.appName = 'App Preview';
        this.appLogoURL = '/img/brand/favicon.png'; // Default logo
        this.showToast('Failed to load app data: ' + (error.message || 'Unknown error'));
      }
    },

    async handleCopyAndRedirect(storeURL) {
      try {
        console.log(`[AppLinks] handleCopyAndRedirect iosAppURL`, this.iosAppURL);
        console.log(`[AppLinks] handleCopyAndRedirect storeURL`, storeURL);
        // First copy the URL to clipboard
        await this.copyToClipboard(this.isAndroid ? this.androidAppURL : this.deepLink); // Android processes the intent URL. iOS processes the dynamic link
        console.log(`Copied to clipboard: ${this.deepLink}`);
        this.showToast('URL has been copied to clipboard.');

        setTimeout(() => {
          console.log(`Copied to clipboard: ${this.deepLink}`);
          window.location.href = this.deepLink;
        }, 1000);
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        this.showToast('Failed to copy URL to clipboard. Continuing to app...');
        // Still navigate even if copy fails
        window.location.href = storeURL;
      }
    },

    copyToClipboard(text) {
      if (navigator.clipboard && window.isSecureContext) {
        // Use the Clipboard API
        return navigator.clipboard.writeText(text);
      } else {
        // Fallback method for older browsers
        let textArea = document.createElement('textarea');
        textArea.value = text;
        // Move textarea out of the viewport
        textArea.style.position = 'absolute';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        return new Promise((resolve, reject) => {
          if (document.execCommand('copy')) {
            resolve();
          } else {
            reject(new Error('Copy command failed'));
          }
          textArea.remove();
        });
      }
    },

    showToast(message) {
      this.toastMessage = message;
      this.toastVisible = true;
      setTimeout(() => {
        this.toastVisible = false;
      }, 3000);
    },
  },
};
</script>

<style scoped>
.app-links-page {
  font-family: Arial, sans-serif;
  text-align: center;
  padding: 20px 10px;
  background-color: #ffffff;
  min-height: 100vh;
}

.app-logo-container {
  background-color: #f9f9f9;
  width: 100%;
  height: 25vh; /* Fixed height matching App Logo */
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  border-radius: 8px;
}

.app-logo {
  width: 25vh;
  height: 25vh;
  object-fit: contain;
}

.content-left {
  text-align: left; /* Left justify the content */
  width: 100%;
  margin-bottom: 20px;
}

.app-name {
  font-weight: bold;
  color: #333333;
  margin-bottom: 10px;
  font-size: 16px;
}

.instruction-text {
  color: #b5b5b5;
  margin-bottom: 20px;
  font-size: 15px;
}

.checkbox-container {
  display: flex;
  align-items: center;
  justify-content: flex-start; /* Align to the left */
  margin: 10px 0;
}

.checkbox-container input {
  margin-right: 10px;
}

.checkbox-container label {
  color: #b5b5b5;
  font-size: 11px;
}

.button {
  padding: 12px 24px;
  font-size: 16px;
  cursor: pointer;
  background-color: #ffffff;
  color: #2db9e6;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: none; /* Remove border */
  border-radius: 4px;
  transition: background-color 0.3s, box-shadow 0.3s;
  margin-bottom: 15px;
  display: block;
  width: 100%;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
}

.button:hover {
  background-color: #f0f8ff;
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
}

.app-link {
  margin-top: 20px;
  text-align: center;
}

.app-link a {
  display: inline-block;
  padding: 12px 24px;
  font-size: 16px;
  text-decoration: none;
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s, box-shadow 0.3s;
  width: 100%;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
  margin-bottom: 10px;
}

.primary-button {
  background-color: #2db9e6;
  color: white;
  padding: 15px 25px;
  border-radius: 8px;
  font-weight: bold;
  font-size: 16px;
  margin: 15px auto;
  display: block;
  text-align: center;
  text-decoration: none;
  width: 80%;
  max-width: 300px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.ios-link {
  background-color: #2db9e6;
  color: white;
}

.store-link {
  background-color: #f0f0f0;
  color: #333;
}

.app-link a:hover {
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
}

/* Remove step indicator styles that are no longer needed */
.step-indicator,
.step-container,
.step,
.step.active,
.step-label,
.platform-section,
.section-title,
.show-all-options {
  display: none;
}

/* Toast Notification Styles */
#toast {
  visibility: hidden;
  min-width: 250px;
  margin-left: -125px;
  background-color: #333;
  color: #fff;
  text-align: center;
  border-radius: 4px;
  padding: 16px;
  position: fixed;
  z-index: 1;
  left: 50%;
  bottom: 30px;
  font-size: 17px;
  opacity: 0;
  transition: opacity 0.5s;
}

#toast.show {
  visibility: visible;
  opacity: 1;
  animation: fadein 0.5s, fadeout 0.5s 2.5s;
}

@keyframes fadein {
  from {
    bottom: 0;
    opacity: 0;
  }
  to {
    bottom: 30px;
    opacity: 1;
  }
}

@keyframes fadeout {
  from {
    bottom: 30px;
    opacity: 1;
  }
  to {
    bottom: 0;
    opacity: 0;
  }
}
</style>
